import { set } from 'lodash/fp';
import { ComponentProps } from 'react';

import NodeAddButton, {
  ClickPayloadType,
} from 'dashboard/components/WorkflowEditor/WorkflowCanvas/NodePlusButton';
import {
  FakeWorkflowStepType,
  outputStep,
  WorkflowStep,
  WorkflowStepConditional,
  WorkflowStepInput,
  WorkflowStepLoop,
  WorkflowStepLoopType,
  WorkflowStepModule,
  WorkflowStepOutput,
  WorkflowStepParallel,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';

export enum StepGraphExtraType {
  ConditionalRoute = 'node-conditional-route',
  PlusButton = 'plus-button',
}

export const BRANCH_ROOT_CENTER = 'root-center';

interface ConditionalRoute {
  type: StepGraphExtraType.ConditionalRoute;
  token: string;
  conditionalStepToken: string;
  title: string;
  routeIndex: number;
}

interface PlusButton {
  type: StepGraphExtraType.PlusButton;
  clickPayload: ComponentProps<typeof NodeAddButton>['clickPayload'];
}

type WorkflowStepWithBranch = WorkflowStepConditional | WorkflowStepLoop | WorkflowStepParallel;
type WorkflowStepWithoutBranch = Exclude<WorkflowStep, WorkflowStepWithBranch | WorkflowStepModule>;

interface StepGraphNode {
  token: string;
  adjacents: StepGraphNode['token'][];
  // eventually we should stop storing whole step to focus on efficient rendering of trees
  step:
    | (WorkflowStepModule &
        (
          | {
              collapsed: false;
              expandable: true;
              moduleBreadths: number;
              outputNodeToken: string;
            }
          | {
              collapsed: true;
              expandable: boolean;
            }
        ))
    | (WorkflowStepWithBranch & { collapsed: boolean })
    | WorkflowStepWithoutBranch
    | WorkflowStepInput
    | WorkflowStepOutput
    | ConditionalRoute
    | PlusButton;
  converging: boolean;
  adjacentBreadths: number[];
  branchRoot: StepGraphNode['token'];
  moduleToken: StepGraphNode['token'] | null;
}

export type KeyedGraph = Record<string, StepGraphNode>;
type StepGraph = StepGraphNode[];

const breadthOfTree = (partialGraph: StepGraph): number =>
  Math.max(
    ...partialGraph.map(({ adjacentBreadths }) => adjacentBreadths.reduce((sum, a) => sum + a, 0)),
    1,
  );

const branchingStepNode = ({
  adjacents,
  branchRoot,
  branchNodes,
  step,
  collapsed,
  moduleToken,
}: Pick<StepGraphNode, 'adjacents' | 'branchRoot'> & {
  branchNodes: StepGraph[];
  step: WorkflowStepWithBranch;
  collapsed: boolean;
  moduleToken: string | null;
}): StepGraphNode => {
  const adjacentBreadths = branchNodes.map(breadthOfTree);

  return {
    token: step.token,
    adjacents,
    step: {
      ...step,
      collapsed,
    },
    converging: false,
    adjacentBreadths: adjacentBreadths.length > 0 ? adjacentBreadths : [1],
    branchRoot,
    moduleToken,
  };
};

const tokenForPlusButtonAfterStep = (tokenPreviousStep: string) =>
  `plus-after-${tokenPreviousStep}`;

const plusButtonAfterStep = ({
  previousNodeToken,
  nextNodeToken,
  converging,
  branchRoot,
  moduleToken,
}: {
  previousNodeToken: string;
  nextNodeToken: string;
  converging: boolean;
  branchRoot: string;
  moduleToken: string | null;
}): StepGraphNode => ({
  token: tokenForPlusButtonAfterStep(previousNodeToken),
  adjacents: [nextNodeToken],
  step: {
    type: StepGraphExtraType.PlusButton,
    clickPayload: {
      type: ClickPayloadType.AfterStep,
      previousNodeToken: previousNodeToken,
    },
  },
  converging,
  adjacentBreadths: [1],
  branchRoot,
  moduleToken,
});

const graphNodesForSteps = (
  steps: WorkflowStep[],
  convergedTo: string,
  branchRoot: string,
  collapsedNodeTokens: string[],
  moduleTokenToBlockSteps: Record<string, WorkflowStep[]>,
  moduleToken: string | null,
): StepGraph => {
  return steps.reduce<StepGraph>((nodes, step, index, allSteps) => {
    const nextStep =
      // TODO: delete this when we don't need to have this fake in steps config
      allSteps[index + 1]?.type === FakeWorkflowStepType.MapOutput
        ? allSteps[index + 2]
        : allSteps[index + 1];
    const converging = !nextStep && branchRoot !== BRANCH_ROOT_CENTER;

    switch (step.type) {
      case WorkflowStepType.Parallel: {
        const plusButtonAfterParallel = plusButtonAfterStep({
          previousNodeToken: step.token,
          nextNodeToken: nextStep?.token ?? convergedTo,
          converging,
          branchRoot,
          moduleToken,
        });

        if (collapsedNodeTokens.includes(step.token)) {
          const collapsedTrunk = branchingStepNode({
            step,
            branchNodes: [],
            adjacents: [plusButtonAfterParallel.token],
            branchRoot,
            collapsed: true,
            moduleToken,
          });

          return [...nodes, collapsedTrunk, plusButtonAfterParallel];
        }

        const branchNodes: StepGraph[] = step.branches.map((branchSteps, i) => [
          {
            token: `plus-after-parallel-start-${step.token}-${i}`,
            adjacents: [branchSteps[0]?.token ?? plusButtonAfterParallel.token],
            step: {
              type: StepGraphExtraType.PlusButton,
              clickPayload: {
                type: ClickPayloadType.FirstInBranch,
                parentNodeToken: step.token,
                branchIndex: i,
              },
            },
            converging: !branchSteps[0],
            adjacentBreadths: [1],
            branchRoot: `plus-after-parallel-start-${step.token}-${i}`,
            moduleToken,
          },
          ...graphNodesForSteps(
            branchSteps,
            plusButtonAfterParallel.token,
            `plus-after-parallel-start-${step.token}-${i}`,
            collapsedNodeTokens,
            moduleTokenToBlockSteps,
            moduleToken,
          ),
        ]);

        const trunk = branchingStepNode({
          step,
          branchNodes,
          adjacents:
            branchNodes.length > 0
              ? branchNodes.map((branchSteps) => branchSteps[0].token)
              : [plusButtonAfterParallel.token],
          branchRoot,
          collapsed: collapsedNodeTokens.includes(step.token),
          moduleToken,
        });

        return [...nodes, trunk, ...branchNodes.flat(), plusButtonAfterParallel];
      }
      case WorkflowStepType.Conditional: {
        const plusButtonAfterConditionalRoutes = plusButtonAfterStep({
          previousNodeToken: step.token,
          nextNodeToken: nextStep?.token ?? convergedTo,
          converging,
          branchRoot,
          moduleToken,
        });

        if (collapsedNodeTokens.includes(step.token)) {
          const collapsedTrunk = branchingStepNode({
            step,
            branchNodes: [],
            adjacents: [plusButtonAfterConditionalRoutes.token],
            branchRoot,
            collapsed: true,
            moduleToken,
          });

          return [...nodes, collapsedTrunk, plusButtonAfterConditionalRoutes];
        }

        const branchNodes: StepGraph[] = step.branches.map((branchSteps, i) =>
          graphNodesForSteps(
            branchSteps,
            plusButtonAfterConditionalRoutes.token,
            step.conditionals[i].token,
            collapsedNodeTokens,
            moduleTokenToBlockSteps,
            moduleToken,
          ),
        );

        const routePillAndPlusButton = step.conditionals.map<StepGraphNode[]>((conditional, i) => [
          {
            token: conditional.token,
            adjacents: [`plus-after-${conditional.token}`],
            step: {
              type: StepGraphExtraType.ConditionalRoute,
              token: conditional.token,
              conditionalStepToken: step.token,
              title: conditional.name || `Route ${i + 1}`,
              routeIndex: i + 1,
            },
            converging: false,
            adjacentBreadths: [1],
            branchRoot: conditional.token,
            moduleToken,
          },
          {
            token: `plus-after-${conditional.token}`,
            adjacents: [branchNodes[i][0]?.token ?? plusButtonAfterConditionalRoutes.token],
            step: {
              type: StepGraphExtraType.PlusButton,
              clickPayload: {
                type: ClickPayloadType.FirstInBranch,
                parentNodeToken: step.token,
                branchIndex: i,
              },
            },
            converging: branchNodes[i].length === 0,
            adjacentBreadths: [1],
            branchRoot: conditional.token,
            moduleToken,
          },
        ]);

        const trunk = branchingStepNode({
          step,
          branchNodes,
          adjacents: routePillAndPlusButton.map((route) => route[0].token),
          branchRoot,
          collapsed: collapsedNodeTokens.includes(step.token),
          moduleToken,
        });

        return [
          ...nodes,
          trunk,
          ...routePillAndPlusButton.flat(),
          ...branchNodes.flat(),
          plusButtonAfterConditionalRoutes,
        ];
      }
      case WorkflowStepType.Loop: {
        const plusButtonAfterLoop = plusButtonAfterStep({
          previousNodeToken: step.token,
          nextNodeToken: nextStep?.token ?? convergedTo,
          converging,
          branchRoot,
          moduleToken,
        });

        if (collapsedNodeTokens.includes(step.token)) {
          const collapsedTrunk = branchingStepNode({
            step,
            branchNodes: [],
            adjacents: [plusButtonAfterLoop.token],
            branchRoot,
            collapsed: true,
            moduleToken,
          });

          return [...nodes, collapsedTrunk, plusButtonAfterLoop];
        }

        const plusButtonAtLoopStart: StepGraphNode = {
          token: `plus-after-loop-start-${step.token}`,
          adjacents: [step.branch[0]?.token ?? plusButtonAfterLoop.token],
          step: {
            type: StepGraphExtraType.PlusButton,
            clickPayload: {
              type: ClickPayloadType.FirstInBranch,
              parentNodeToken: step.token,
              branchIndex: 0,
            },
          },
          converging: !step.branch[0],
          adjacentBreadths: [1],
          branchRoot: `plus-after-loop-start-${step.token}`,
          moduleToken,
        };

        const outputStepToken =
          (step.settings.type === WorkflowStepLoopType.Map && step.settings['output-step-token']) ||
          `${step.token}-map-output`;
        const loopedNodes = [
          plusButtonAtLoopStart,
          ...graphNodesForSteps(
            step.branch,
            plusButtonAfterLoop.token,
            plusButtonAtLoopStart.branchRoot,
            collapsedNodeTokens,
            moduleTokenToBlockSteps,
            moduleToken,
          ),
        ].map((graphNode) =>
          // overwrite converging step of children of map loop to connect to injected "output" step
          graphNode.adjacents[0] === plusButtonAfterLoop.token
            ? step.settings.type === WorkflowStepLoopType.Map
              ? {
                  ...graphNode,
                  adjacents: [outputStepToken],
                  converging: graphNode.branchRoot !== plusButtonAtLoopStart.token,
                }
              : {
                  ...graphNode,
                  adjacents: [...graphNode.adjacents, step.token],
                }
            : graphNode,
        );

        const trunk = branchingStepNode({
          step,
          branchNodes: [[], loopedNodes],
          adjacents: loopedNodes[0] ? [loopedNodes[0].token] : [plusButtonAfterLoop.token],
          branchRoot,
          collapsed: false,
          moduleToken,
        });

        const output: StepGraph =
          step.settings.type === WorkflowStepLoopType.Map
            ? [
                {
                  token: outputStepToken,
                  adjacents: [plusButtonAfterLoop.token, step.token],
                  step: {
                    type: FakeWorkflowStepType.MapOutput,
                    token: outputStepToken,
                    name: 'Array Map Output',
                  },
                  converging: true,
                  adjacentBreadths: [1],
                  branchRoot: plusButtonAtLoopStart.branchRoot,
                  moduleToken,
                },
              ]
            : [];

        return [...nodes, trunk, ...loopedNodes, ...output, plusButtonAfterLoop];
      }
      // TODO: delete this when we don't need to have this fake in steps config
      case FakeWorkflowStepType.MapOutput:
        return nodes;
      case WorkflowStepType.Module: {
        const plusButtonAfterModule = plusButtonAfterStep({
          previousNodeToken: step.token,
          nextNodeToken: nextStep?.token ?? convergedTo,
          converging,
          branchRoot,
          moduleToken,
        });
        const showNestedSteps =
          collapsedNodeTokens.includes(step.token) &&
          moduleTokenToBlockSteps[step.token]?.length > 0;

        if (!showNestedSteps) {
          return [
            ...nodes,
            {
              token: step.token,
              adjacents: [plusButtonAfterModule.token],
              step: {
                ...step,
                collapsed: true,
                expandable: moduleTokenToBlockSteps[step.token]?.length > 0,
                name: step.name,
              },
              converging: false,
              adjacentBreadths: [1],
              branchRoot,
              moduleToken: step.token,
            },
            plusButtonAfterModule,
          ];
        }

        const plusButtonBeforeNestedSteps = plusButtonAfterStep({
          previousNodeToken: step.token + '-inner',
          nextNodeToken: moduleTokenToBlockSteps[step.token][0].token,
          converging: false,
          branchRoot,
          moduleToken: step.token,
        });

        const moduleOutput: StepGraphNode = {
          token: `${step.token}-${outputStep.token}`,
          adjacents: [plusButtonAfterModule.token],
          step: {
            ...outputStep,
            name: 'Module Output',
            token: `${step.token}-${outputStep.token}`,
          } satisfies WorkflowStepOutput,
          converging: false,
          adjacentBreadths: [1],
          branchRoot,
          moduleToken: step.token,
        };

        const nestedSteps = graphNodesForSteps(
          moduleTokenToBlockSteps[step.token],
          moduleOutput.token,
          branchRoot,
          collapsedNodeTokens,
          moduleTokenToBlockSteps,
          step.token,
        );

        return [
          ...nodes,
          {
            token: step.token,
            adjacents: [plusButtonBeforeNestedSteps.token],
            step: {
              ...step,
              collapsed: false,
              expandable: true,
              name: step.name,
              moduleBreadths: breadthOfTree(nestedSteps),
              outputNodeToken: moduleOutput.token,
            },
            converging: false,
            adjacentBreadths: [1],
            branchRoot,
            moduleToken: step.token,
          },
          plusButtonBeforeNestedSteps,
          // Set converging: false for the last of nested steps to connect to moduleOutput straightly
          ...set(`[${nestedSteps.length - 1}].converging`, false, nestedSteps),
          moduleOutput,
          plusButtonAfterModule,
        ];
      }
      default: {
        const plusButton = plusButtonAfterStep({
          previousNodeToken: step.token,
          nextNodeToken: nextStep?.token ?? convergedTo,
          converging,
          branchRoot,
          moduleToken,
        });

        return [
          ...nodes,
          {
            token: step.token,
            adjacents: [plusButton.token],
            step,
            converging: false,
            adjacentBreadths: [1],
            branchRoot,
            moduleToken,
          },
          plusButton,
        ];
      }
    }
  }, []);
};

export const buildStepGraph = ({
  steps,
  inputStep,
  collapsedNodeTokens,
  moduleTokenToBlockSteps,
}: {
  steps: WorkflowStep[];
  inputStep: WorkflowStepInput;
  collapsedNodeTokens: string[];
  moduleTokenToBlockSteps: Record<string, WorkflowStep[]>;
}) => {
  const graph: StepGraph = [
    {
      token: inputStep.token,
      adjacents: [tokenForPlusButtonAfterStep(inputStep.token)],
      step: inputStep,
      converging: false,
      adjacentBreadths: [1],
      branchRoot: BRANCH_ROOT_CENTER,
      moduleToken: null,
    },
    {
      token: tokenForPlusButtonAfterStep(inputStep.token),
      adjacents: steps.length > 0 ? [steps[0].token] : ['output'],
      step: {
        type: StepGraphExtraType.PlusButton,
        clickPayload: {
          type: ClickPayloadType.AfterStep,
          previousNodeToken: inputStep.token,
        },
      },
      converging: false,
      adjacentBreadths: [1],
      branchRoot: BRANCH_ROOT_CENTER,
      moduleToken: null,
    },
    ...graphNodesForSteps(
      steps,
      outputStep.token,
      BRANCH_ROOT_CENTER,
      collapsedNodeTokens,
      moduleTokenToBlockSteps,
      null,
    ),
    {
      token: outputStep.token,
      adjacents: [],
      step: outputStep,
      converging: false,
      adjacentBreadths: [],
      branchRoot: BRANCH_ROOT_CENTER,
      moduleToken: null,
    },
  ];

  // TODO: graph itself should be like this instead of computing here
  return graph.reduce<KeyedGraph>((kv, graphNode) => ({ ...kv, [graphNode.token]: graphNode }), {});
};
